/** @jsx jsx */
import { jsx } from '@emotion/react'
import styled from '@emotion/styled';
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createRoot } from 'react-dom/client'
import { CacheProvider, css, CSSObject } from '@emotion/react'
import createCache from '@emotion/cache'
import { pageLanguage, siteUrls, useDatasets } from './Data';
import { useFile } from './useFile';
import { link_more } from 'components/langutils';
import { projectHref, recordTeaser } from './langutils';
import { Arrow, LinkListSection, OutlineButton, PictureTile, TileGrid, courseLabel, coursePicture, teaserOf, tilePictures } from './UzlDesign';


const PictureCardBase = styled('div')(({image} : any) => ({
  backgroundImage: image? `url(${image})` : '',
  backgroundSize: "contain",
  backgroundRepeat: "repeat",
  margin: '24px',
  backgroundColor: 'rgba(0,0,0,0.7)',
  '&:hover': {
    backgroundColor: 'rgba(0,0,0,0.5)',
  },
}))

const PictureCardContent = styled('div')((): CSSObject => ({
  position: 'relative',
  background: 'var(--iti-bg)',
  color: 'var(--iti-text)',
  textOverflow: 'ellipsis ellipsis',
  height: '160px',
  padding: '12px',
  filter: 'drop-shadow(0px 6px 10px black)',
  '&:hover': {
    filter: 'drop-shadow(0px 4px 5px black)',
  },
  '& > .teaser': {
    textOverflow: '...',
    overflow: 'hidden',
    position: 'absolute',
    top: '0.8em',
    bottom: '2em',
    right: '2em',
    left: '40px',
  },
  '& > .more': {
    position: 'absolute',
    bottom: '0em',
    textDecoration: 'underline',
  },
  '& > h3': {
    color: 'var(--iti-heading)',
    fontSize: '16px',
    lineHeight: '18px',
    marginTop: 0,
    marginBottom: '4px',
    fontWeight: 'normal',
  },
  '&.right': {
    paddingRight: '40px',
    marginRight: '35%',
  },
  '&.left': {
    paddingLeft: '40px',
    marginLeft: '35%',
  },
  '::after': {
    content: "' '",
    position: 'absolute',
    top: 0,
    backgroundColor: 'var(--iti-bg)',
    width: '40px',
    bottom: 0,
  },
  '&.right::after': {
    right: '-20px',
    transform: 'skew(-10deg)',
  },
  '&.left::after': {
    left: '-20px',
    transform: 'skew(10deg)',
  },
}))

const PictureCard = (props: any) => {
  return (
    <PictureCardBase image={props.image} onClick={props.onClick} style={{backgroundPosition: props.pos ?? 'right', ...((props.href||props.onClick)?{cursor: 'pointer'}:{})}}>
      <PictureCardContent className={props.pos ?? 'right'}>
        { props.children }
      </PictureCardContent>
    </PictureCardBase>
  )
}

export const PictureCardElement = ({pos, image, href, styleRoot}: any) => {
  const myCache = createCache({
    key: 'picturecard', container: styleRoot
  })
  const navigate = (target: string|undefined|null) => {
    if(typeof target === 'string') {
      location.href = target
    }
  }
  useEffect(() => {
    return () => {
      myCache.sheet.flush();
      (myCache.inserted as any) = [];
      myCache.registered = {};
    }
  })
  return (
    <CacheProvider value={myCache}>
      <PictureCard pos={pos} image={image} onClick={navigate(href)}>
        <slot name="picturecard" />
      </PictureCard>
    </CacheProvider>
  )
}

export const ProjectCardListElement = ({styleRoot}: any) => {
  const lang = pageLanguage();
  const urls = siteUrls(lang);
  const [files] = useDatasets(urls, ['projects']);
  const data = { ...files, lang, urls } as any;
  const myCache = createCache({
    key: 'picturecard', container: styleRoot
  })
  useEffect(() => {
    return () => {
      myCache.sheet.flush();
      (myCache.inserted as any) = [];
      myCache.registered = {};
    }
  })
  const running = (data?.projects ?? []).filter((project: any) => project.active);
  const en = data?.lang === 'en';
  // the running projects as picture tiles, like the university's study areas
  return (
    <CacheProvider value={myCache}>
      <TileGrid css={{ padding: '0 24px' }}>
        {running.map((project: any) => {
          const { image, logo } = tilePictures(project);
          return <PictureTile key={project.name} title={project.name} image={image} logo={logo}
            teaser={recordTeaser(project, data.lang) ?? teaserOf(project[`description_${data.lang}`], 120)}
            href={projectHref(project, data.lang)} />;
        })}
      </TileGrid>
      <div css={{ padding: '0 24px 24px' }}>
        <OutlineButton href={data?.urls?.projectroot}>{en ? 'All projects' : 'Alle Projekte'}<Arrow /></OutlineButton>
      </div>
    </CacheProvider>
  )
}

// courses.json lists the semesters; take the latest real one ("abc" sorts them,
// "zzzzz" marks the semester-independent courses)
function latestSemester(semesters: any) {
  const all = Object.values(semesters?.courses ?? {}) as any[];
  return all.filter((s) => s.abc !== 'zzzzz').sort((a, b) => a.abc < b.abc ? 1 : -1)[0];
}

export const CourseCardListElement = ({styleRoot}: any) => {
  const lang = pageLanguage();
  const urls = siteUrls(lang);
  const [files] = useDatasets(urls, ['semesters']);
  const data = { ...files, lang, urls } as any;
  const semester = latestSemester(data?.semesters);
  const [courses] = useFile(semester && `${data.urls.dataroot}static/courses-${semester.id}.json`, (r) => r.json());
  const myCache = createCache({
    key: 'coursecard', container: styleRoot
  })
  useEffect(() => {
    return () => {
      myCache.sheet.flush();
      (myCache.inserted as any) = [];
      myCache.registered = {};
    }
  })
  // the latest semester's lectures as a link list, with a course picture next to it
  const list = courses?.courses ?? [];
  const picture = list.map(coursePicture).find(Boolean);
  return (
    <CacheProvider value={myCache}>
      <div css={{ margin: '24px', padding: '24px 28px 4px', background: 'var(--iti-bg)', color: 'var(--iti-text)',
                  '& h2': { color: 'var(--iti-heading)' } }}>
        <LinkListSection title={semester?.name} image={picture}
          items={list.map((course: any) => ({ ...courseLabel(course), href: course.link }))} />
      </div>
    </CacheProvider>
  )
}

function PictureCardTestBox() {
  return (
    <div>
    <PictureCard>
      Bacon ipsum dolor amet rump pork bacon hamburger, pastrami frankfurter meatball strip steak shankle salami shank shoulder burgdoggen ball tip brisket. Turkey bresaola sirloin ribeye rump. Venison leberkas filet mignon ham buffalo
    </PictureCard>
    <PictureCard pos={"left"}>
      Bacon ipsum dolor amet rump pork bacon hamburger, pastrami frankfurter meatball strip steak shankle salami shank shoulder burgdoggen ball tip brisket. Turkey bresaola sirloin ribeye rump. Venison leberkas filet mignon ham buffalo
    </PictureCard>

    </div>
  )
}

export const PictureCardTest = () => {
  // Before REACT start, clean Frames
  const test = document.getElementById('test');
  if (test) {
    test.innerHTML = "";
  }
  createRoot(test!).render(<PictureCardTestBox />)
}